let ghostEl = null;

export function setDragGhost(node) {
  ghostEl = node;
}

function clearZones() {
  document.querySelectorAll('[data-zone]').forEach((zone) => {
    zone.classList.remove('is-over');
    zone.style.removeProperty('--marker');
  });
}

function zoneFromPoint(x, y) {
  const zones = document.querySelectorAll('[data-zone]');
  for (let i = zones.length - 1; i >= 0; i -= 1) {
    const rect = zones[i].getBoundingClientRect();
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return zones[i];
  }
  return null;
}

function placeInZone(zone, y, sourceEl) {
  const scroller = zone.querySelector('[data-scroller]') || zone;
  const length = Number(zone.dataset.length || 0);
  const rows = [...zone.querySelectorAll('[data-index]')].filter(
    (row) => row !== sourceEl && !row.hidden && row.getBoundingClientRect().height > 0
  );

  let index = length;
  let markerTop = rows.length ? rows[rows.length - 1].offsetTop + rows[rows.length - 1].offsetHeight : 0;

  for (const row of rows) {
    const rect = row.getBoundingClientRect();
    const rowIndex = Number(row.dataset.index);
    if (!Number.isFinite(rowIndex)) continue;
    if (y < rect.top + rect.height / 2) {
      index = Math.min(rowIndex, length);
      markerTop = row.offsetTop;
      break;
    }
    if (rowIndex >= length) {
      index = length;
      markerTop = row.offsetTop;
      break;
    }
    index = rowIndex + 1;
    markerTop = row.offsetTop + row.offsetHeight;
  }

  return { index, markerTop, scroller };
}

function targetFromPoint(x, y, sourceEl) {
  const zone = zoneFromPoint(x, y);
  document.querySelectorAll('[data-zone].is-over').forEach((el) => {
    if (el !== zone) el.classList.remove('is-over');
  });
  if (!zone) {
    clearZones();
    return null;
  }

  const { index, markerTop, scroller } = placeInZone(zone, y, sourceEl);
  const rect = scroller.getBoundingClientRect();
  if (y > rect.bottom - 28) scroller.scrollTop += 14;
  else if (y < rect.top + 28 && y > rect.top) scroller.scrollTop -= 14;

  zone.classList.add('is-over');
  zone.style.setProperty('--marker', `${markerTop}px`);
  if (!Number.isFinite(index)) return null;

  return {
    zone: zone.dataset.zone,
    date: zone.dataset.date || undefined,
    pageId: zone.dataset.pageId || undefined,
    listId: zone.dataset.listId || undefined,
    index,
  };
}

export function startPointerDrag({ event, text, rowEl, source, onDrop }) {
  if (event.button !== 0) return;
  if (event.target.closest?.('[data-no-drag]')) return;
  if (document.body.classList.contains('is-dragging')) return;

  const startX = event.clientX;
  const startY = event.clientY;
  const pointerId = event.pointerId;
  let active = false;
  let pending = null;
  let ended = false;

  const track = (x, y) => {
    if (ghostEl) ghostEl.style.transform = `translate3d(${x + 14}px, ${y + 12}px, 0)`;
    pending = targetFromPoint(x, y, rowEl);
  };

  const activate = () => {
    active = true;
    document.body.classList.add('is-dragging');
    rowEl?.classList.add('is-source');
    if (ghostEl) {
      ghostEl.hidden = false;
      ghostEl.dir = 'auto';
      ghostEl.textContent = text || '';
    }
    window.getSelection()?.removeAllRanges();
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && rowEl?.contains(focused)) focused.blur();
    try {
      rowEl?.setPointerCapture?.(pointerId);
    } catch {
      /* The pointer can already be gone if the gesture ended. */
    }
  };

  const finish = (point) => {
    if (ended) return;
    ended = true;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onCancel);
    window.removeEventListener('keydown', onKey);
    if (active && point) {
      const next = targetFromPoint(point.x, point.y, rowEl);
      if (next) pending = next;
    }
    if (active && pending) onDrop(source, pending);
    document.body.classList.remove('is-dragging');
    rowEl?.classList.remove('is-source');
    if (ghostEl) ghostEl.hidden = true;
    clearZones();
    try {
      if (rowEl?.hasPointerCapture?.(pointerId)) rowEl.releasePointerCapture(pointerId);
    } catch {
      /* Capture may already have been released. */
    }
  };

  const onMove = (moveEvent) => {
    if (ended) return;
    const dx = moveEvent.clientX - startX;
    const dy = moveEvent.clientY - startY;
    if (!active) {
      if (dx * dx + dy * dy < 16) return;
      moveEvent.preventDefault();
      activate();
      if (ended) return;
    }
    track(moveEvent.clientX, moveEvent.clientY);
  };

  const onUp = (upEvent) => {
    finish(active ? { x: upEvent.clientX, y: upEvent.clientY } : null);
  };

  const onCancel = () => finish(null);

  const onKey = (keyEvent) => {
    if (keyEvent.key !== 'Escape') return;
    keyEvent.preventDefault();
    pending = null;
    finish(null);
  };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
  window.addEventListener('keydown', onKey);
}
